import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { FormattedMessage } from 'react-intl';
import { BulkUpdateValidationFeedback } from '../../../sharedComponents/bulkStatusUpdate/BulkUpdateValidationFeedback';
import { BulkStatusError } from '../../../sharedComponents/bulkStatusUpdate/BulkStatusErrorMessage';
import { ServiceOrderBulkUpdatePreviewErrorCode, ServiceOrderBulkUpdatePreviewResult } from '../serviceOrder.types';
import { BulkUpdateMaxSizeErrorMessage } from '../../../sharedComponents/bulkStatusUpdate/BulkUpdateMaxSizeErrorMessage';
import { SERVICE_ORDER_MAX_SELECTION_SIZE } from './ServiceOrderBulkSelectionDropdown';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import { useState } from 'react';
import {
    SERVICE_BULK_UPDATE_FORM_ID,
    ServiceOrderBulkUpdateFormState,
    ServiceOrderBulkUpdateFormStep,
} from './ServiceOrderBulkUpdateFormStep';
import { useAppDispatch } from '../../../../../configuration/setup/hooks';
import { serviceOrdersSlice } from '../../../reducers/serviceOrdersSlice';
import { DateParseErrors, initialDateParseErrors, ServiceOrderDateField } from '../shared/ServiceOrderFormValidation';

type ServiceOrderBulkUpdateDialogProps = {
    readonly previewResult: ServiceOrderBulkUpdatePreviewResult;
    readonly onClose: () => void;
};

enum ServiceOrderBulkUpdateDialogStep {
    VALIDATION = 'validation',
    FORM = 'form',
}

// TODO change translation
const UpdateButton = ({ disabled }: { disabled: boolean }) => (
    <Button type="submit" form={SERVICE_BULK_UPDATE_FORM_ID} disabled={disabled} iconName="rioglyph rioglyph-ok">
        <FormattedMessage id="outboundOrderBook.common.button.update" />
    </Button>
);

const CloseButton = ({ onClick }: { onClick: () => void }) => (
    <Button onClick={onClick}>
        <FormattedMessage id="outboundOrderBook.common.button.cancel" />
    </Button>
);

export const ServiceOrderBulkUpdateDialog = ({ previewResult, onClose }: ServiceOrderBulkUpdateDialogProps) => {
    const dispatch = useAppDispatch();
    const [currentStep, setCurrentStep] = useState(ServiceOrderBulkUpdateDialogStep.VALIDATION);
    const [dateParseErrors, setDateParseErrors] = useState<DateParseErrors>(initialDateParseErrors);
    const setDateParseError = (field: ServiceOrderDateField, hasParseError: boolean) => {
        setDateParseErrors((current) => ({ ...current, [field]: hasParseError }));
    };
    const isValidationStep = currentStep === ServiceOrderBulkUpdateDialogStep.VALIDATION;
    const isSuccess = previewResult.success;
    const validOrdersCount = isSuccess ? previewResult.serviceOrders.length : 0;
    const invalidOrders: BulkStatusError<ServiceOrderBulkUpdatePreviewErrorCode>[] = isSuccess
        ? previewResult.invalidOrders.map((error) => ({
              errorCode: error.errorCode,
              identifiers: error.orderIds,
              identifierType: 'ORDER',
          }))
        : [];
    const nextButtonDisabled = !isSuccess || validOrdersCount === 0;

    const requestError =
        !isSuccess && previewResult.errorCode === 'size-exceeded' ? (
            <BulkUpdateMaxSizeErrorMessage identifierType="ORDER" maxBulkSize={SERVICE_ORDER_MAX_SELECTION_SIZE} />
        ) : undefined;

    // TODO PORTALOUT-3962
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleOnSave = (formState: ServiceOrderBulkUpdateFormState) => {
        dispatch(serviceOrdersSlice.actions.clearBulkSelection());
        onClose();
    };

    return (
        <Dialog show onClose={onClose} bsSize={Dialog.SIZE_LG}>
            <Dialog.Title
                title={<FormattedMessage id="outboundOrderBook.transportAssignments.bulkUpdate.dialogTitle" />}
            />
            <Dialog.Body>
                {isValidationStep ? (
                    <BulkUpdateValidationFeedback
                        validItemsCount={validOrdersCount}
                        invalidItems={invalidOrders}
                        requestError={requestError}
                    />
                ) : (
                    isSuccess && (
                        <ServiceOrderBulkUpdateFormStep
                            validOrders={previewResult.serviceOrders}
                            setDateParseError={setDateParseError}
                            isDisabled={false} // TODO PORTALOUT-3962 disable during bulking creation request
                            handleOnSave={handleOnSave}
                        />
                    )
                )}
            </Dialog.Body>
            <Dialog.Footer>
                <ButtonToolbar>
                    <CloseButton onClick={onClose} />
                    {isValidationStep ? (
                        <Button
                            bsStyle={Button.PRIMARY}
                            onClick={() => setCurrentStep(ServiceOrderBulkUpdateDialogStep.FORM)}
                            disabled={nextButtonDisabled}
                        >
                            <FormattedMessage id="outboundOrderBook.common.button.next" />
                        </Button>
                    ) : (
                        <UpdateButton disabled={Object.values(dateParseErrors).some(Boolean)} />
                    )}
                </ButtonToolbar>
            </Dialog.Footer>
        </Dialog>
    );
};
